// @ts-nocheck
/**
 * <personalisation-props> Custom Element
 *
 * Manages personalisation fields (radio Yes/No toggle + text input).
 * Uses Liquid-rendered inputs with form attribute association.
 * Blocks submit buttons when personalisation is invalid.
 */

/* ────────────────────────────────────────────────────────────
 *  PersonalisationGroup — controls a single field
 * ──────────────────────────────────────────────────────────── */
class PersonalisationGroup {
  constructor(el, onChange) {
    this.el = el;
    this.idx = el.dataset.personalisationGroup;
    this.radios = el.querySelectorAll('input[type="radio"]');
    this.body = el.querySelector(".personalisation-prop__body");
    this.input = el.querySelector(".personalisation-prop__input");
    this.counter = el.querySelector(".personalisation-prop__counter");
    this.errorEl = el.querySelector(".personalisation-prop__error");
    this.emptyInput = el.querySelector(
      `.personalisation-prop__empty[data-index="${this.idx}"]`,
    );
    this.maxLen = parseInt(this.input.dataset.maxlength) || 0;
    this.active = false;
    this.onChange = onChange;

    this.#bind();
  }

  open() {
    this.active = true;
    this.body.classList.add("is-open");
    this.input.disabled = false;
    this.input.value = "";
    this.input.required = true;
    this.emptyInput.disabled = true;
    if (this.proxy) {
      this.proxy.value = "";
      this.proxy.disabled = false;
    }
    this.onChange?.();
    setTimeout(() => this.input.focus(), 320);
  }

  close() {
    this.active = false;
    this.body.classList.remove("is-open");
    this.input.disabled = true;
    this.input.required = false;
    this.input.value = "";
    this.emptyInput.disabled = false;
    if (this.proxy) {
      this.proxy.value = this.emptyInput.value;
      this.proxy.disabled = false;
    }
    this.clearError();
    this.onChange?.();
  }

  isValid() {
    if (!this.active) return true;
    const val = this.input.value.trim();
    return val.length > 0 && (!this.maxLen || val.length <= this.maxLen);
  }

  showError(msg) {
    this.errorEl.textContent = msg;
    this.el.classList.add("has-error");
    this.input.setCustomValidity(msg);
  }

  clearError() {
    this.errorEl.textContent = "";
    this.el.classList.remove("has-error");
    this.input.setCustomValidity("");
    if (this.counter) this.#updateCounter(0);
  }

  validate() {
    this.clearError();
    if (!this.active) return true;

    const val = this.input.value.trim();
    if (!val) {
      const label = this.input.placeholder
        .replace("Enter your ", "")
        .replace(" here", "");
      this.showError(`Please enter your ${label}`);
      return false;
    }
    if (this.maxLen && val.length > this.maxLen) {
      this.showError(`Maximum ${this.maxLen} characters allowed`);
      return false;
    }
    return true;
  }

  #updateCounter(len) {
    if (!this.counter) return;
    this.counter.textContent = `${len}/${this.maxLen}`;
    this.counter.classList.toggle("is-limit", len > this.maxLen);
  }

  #bind() {
    this.radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        radio.value === "yes" ? this.open() : this.close();
      });
    });

    this.input.addEventListener("input", () => {
      const len = this.input.value.length;
      this.#updateCounter(len);
      if (this.proxy) this.proxy.value = this.input.value;

      if (this.maxLen && len > this.maxLen) {
        this.showError(
          `Maximum ${this.maxLen} characters allowed (${len - this.maxLen} over)`,
        );
      } else if (this.el.classList.contains("has-error")) {
        this.validate();
      }
      this.onChange?.();
    });

    this.input.addEventListener("blur", () => {
      if (this.active) this.validate();
    });

    this.input.addEventListener("invalid", (e) => {
      e.preventDefault();
      this.validate();
      this.input.focus();
      this.el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }
}

/* ────────────────────────────────────────────────────────────
 *  <personalisation-props> Custom Element
 * ──────────────────────────────────────────────────────────── */
class PersonalisationProps extends HTMLElement {
  #groups = [];
  #form = null;
  #observer = null;

  connectedCallback() {
    this.#initGroups();

    this.#form = this.#findForm();
    if (this.#form) {
      this.#bindForm();
    } else {
      this.#watchForForm();
    }

    // Re-sync sticky button after variant update (morph resets DOM)
    const section = this.closest(".shopify-section");
    if (section) {
      section.addEventListener("variant:update", () => {
        requestAnimationFrame(() => this.#syncSubmitState());
      });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#groups.forEach((g) => g.proxy?.remove());
    this.#groups = [];
    this.#form = null;
  }

  #watchForForm() {
    const container =
      this.closest(".product-details, .quick-add-modal__content") ||
      this.parentElement;
    if (!container) return;

    this.#observer = new MutationObserver(() => {
      this.#form = this.#findForm();
      if (this.#form) {
        this.#observer.disconnect();
        this.#observer = null;
        this.#bindForm();
      }
    });

    this.#observer.observe(container, { childList: true, subtree: true });
  }

  #initGroups() {
    this.#groups = [];
    this.querySelectorAll(".personalisation-prop").forEach((el) => {
      this.#groups.push(
        new PersonalisationGroup(el, () => this.#syncSubmitState()),
      );
    });
  }

  #findForm() {
    const ancestorFormComponent = this.closest("product-form-component");
    if (ancestorFormComponent) {
      const f = ancestorFormComponent.querySelector(
        'form[data-type="add-to-cart-form"]',
      );
      if (f?.tagName === "FORM") return f;
    }

    const container = this.closest(
      ".product-details, #quick-add-modal-content, .quick-add-modal__content",
    );
    if (container) {
      const f = container.querySelector(
        'product-form-component form[data-type="add-to-cart-form"]',
      );
      if (f?.tagName === "FORM") return f;
    }

    const input = this.querySelector(".personalisation-prop__input");
    if (!input) return null;
    const formId = input.getAttribute("form");
    const f = formId ? document.getElementById(formId) : null;
    return f?.tagName === "FORM" ? f : null;
  }

  #bindForm() {
    const modal = this.closest(".quick-add-modal__content");
    if (modal && this.#form) {
      // Quickview modal has duplicate form ID with product page.
      // form attribute resolves to first match in DOM (product page form),
      // so inputs aren't included in quickview's FormData.
      // Fix: create hidden proxy inputs INSIDE the form.
      this.#groups.forEach((g) => {
        g.input.removeAttribute("form");
        g.emptyInput.removeAttribute("form");

        const proxy = document.createElement("input");
        proxy.type = "hidden";
        proxy.name = g.input.name;
        proxy.value = g.emptyInput.value; // default: "Customer requires no text"
        this.#form.appendChild(proxy);
        g.proxy = proxy;
      });
    }

    this.#syncSubmitState();
  }

  #hasActiveInvalid() {
    return this.#groups.some((g) => g.active && !g.isValid());
  }

  #getContainer() {
    return this.closest(
      ".product-details, .quick-add-modal__content, #quick-add-modal-content",
    );
  }

  #syncSubmitState() {
    if (!this.#form) {
      this.#form = this.#findForm();
      if (this.#form) {
        this.#bindForm();
        return; // #bindForm already calls #syncSubmitState
      }
    }

    const invalid = this.#hasActiveInvalid();
    const container = this.#getContainer();
    if (!container) return;

    container
      .querySelectorAll('button[type="submit"][name="add"]')
      .forEach((btn) => {
        btn.disabled = invalid;
      });

    const accel = container.querySelector(".accelerated-checkout-block");
    if (accel) {
      accel.style.pointerEvents = invalid ? "none" : "";
      accel.style.opacity = invalid ? "0.5" : "";
    }

    // Block sticky add-to-cart button
    const section = this.closest(".shopify-section");
    if (section) {
      const stickyBtn = section.querySelector(
        "sticky-add-to-cart [ref='addToCartButton']",
      );
      if (stickyBtn) {
        stickyBtn.disabled = invalid;
      }
    }

    if (invalid) {
      this.#groups.forEach((g) => {
        if (g.active && !g.isValid()) g.validate();
      });
    }
  }
}

if (!customElements.get("personalisation-props")) {
  customElements.define("personalisation-props", PersonalisationProps);
}
